/**
 * Os vinte e quatro relatórios, descritos em vez de codificados.
 *
 * Todos têm a mesma casca — escolhe o período, roda, olha o resultado —, então
 * o que muda de um para outro é: o endereço, os filtros e a forma da saída.
 * Isso é dado, não código, e fica aqui. Assim não existem vinte e quatro telas
 * quase iguais para manter em sincronia.
 *
 * **Os números vieram de medição, não de estimativa.** Cada `custo` e cada
 * `volume` abaixo saiu de rodar o endpoint contra produção num mês de agosto de
 * 2026. Onde a resposta é grande ou lenta a tela avisa antes, porque esperar 45
 * segundos sem saber que vai esperar é o que faz a pessoa achar que travou.
 */

/**
 * As duas leituras que um gráfico pode ter aqui.
 *
 * **`top`** responde "quem é o maior": soma por rótulo, ordena por valor e corta
 * nos 15. **`tempo`** responde "está crescendo?": ordena por data e põe cada
 * ponto na posição real dele no calendário.
 *
 * Trocar um pelo outro estraga o relatório. O `top` numa série mensal embaralha
 * a ordem do tempo — e o `tempo` num relatório de sistemas não tem eixo.
 */
export type Grafico =
  | {
      tipo?: "top"
      /** Caminho do array na resposta. Vazio = a resposta é o próprio array. */
      de?: string
      rotulo: string
      valor: string
      titulo?: string
    }
  | {
      tipo: "tempo"
      de: string
      valor: string
      titulo?: string
      /** Onde estão o ano e o mês na linha. */
      ano?: string
      mes?: string
    }

export type Filtro =
  | "periodo" | "categoriaVeiculo" | "assunto" | "sistema" | "marca" | "cliente"
  | "somenteErros"
  /** Multi-seleção de texto. Os valores são os do banco, não ids. */
  | "especialidades" | "estados"

/** Como desenhar a resposta. */
export type Saida =
  | { forma: "tabela"; colunas: Coluna[]; de?: string }
  | { forma: "tiles"; campos: { chave: string; rotulo: string }[] }
  | { forma: "series"; series: { chave: string; rotulo: string; colunas: Coluna[] }[] }
  | { forma: "composta"; blocos: BlocoComposto[] }

export type Coluna = {
  chave: string
  rotulo: string
  /** Números alinham à direita e ganham separador de milhar. */
  numero?: boolean
  /**
   * Ano é número mas não leva separador de milhar: "2.007" não é ano nenhum.
   * Continua alinhado à direita como os outros números.
   */
  ano?: boolean
  largura?: number
}

export type BlocoComposto = {
  rotulo: string
  /** Caminho dentro da resposta: `itens`, `serieTemporal.periodos`. */
  de: string
  colunas: Coluna[]
}

export type Relatorio = {
  chave: string
  nome: string
  grupo: string
  rota: string
  /** O que a pessoa escolhe antes de rodar. `periodo` vale por dois campos. */
  filtros: Filtro[]
  saida: Saida
  /** Uma linha dizendo o que o relatório responde. */
  oQueResponde: string
  /** Medido: quanto demorou e quanto voltou, num mês. Aparece antes de rodar. */
  custo?: { segundos: number; linhas: number }
  /** Aviso honesto sobre o dado, quando há um. */
  ressalva?: string
  /**
   * Os gráficos, onde o sistema antigo tem um. Lista porque há relatório com
   * dois: o de crescimento desenha uma série para o assunto e outra para o par
   * assunto × sistema, como as duas telas do legado.
   */
  graficos?: Grafico[]
  /** Teto de período que a API impõe. */
  maximoDias?: number
  /**
   * O verbo. Quase todos são GET; o de licença por estado é POST porque leva
   * duas listas no corpo — **e mesmo assim não grava nada**.
   */
  metodo?: "post"
  /**
   * Abre o detalhe de uma linha. `campoId` é a chave da linha que vira `{id}`
   * na rota — e não é sempre o `id`: no log da DiagIA a rota quer o id da
   * *conversa*, que vem em outro campo.
   */
  detalhe?: { rota: string; campoId: string; titulo: string; nota?: string }
  /**
   * Um cabeçalho lido da **própria resposta**, acima das tabelas.
   *
   * Diferente da `ressalva`, que é texto fixo: aqui o aviso depende do que veio.
   * Nasceu do consumo da DiagIA, onde a cobertura da medição muda todo mês e
   * precisa ser lida antes do número — 92% em junho, 4% em setembro.
   */
  painel?: {
    /** `chave` aceita caminho com ponto: `cobertura.percentual`. */
    campos: { chave: string; rotulo: string; sufixo?: string }[]
    /** Aparece quando a chave apontada vier **falsa**. */
    alerta?: { chave: string; texto: string }
  }
}

const qtd = (rotulo = "Acessos"): Coluna => ({ chave: "quantidade", rotulo, numero: true, largura: 110 })

export const RELATORIOS: Relatorio[] = [
  // ---------------------------------------------------------- acesso e consumo
  {
    chave: "acesso-sistema",
    nome: "Acesso ao sistema",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/sistema",
    filtros: ["periodo", "categoriaVeiculo"],
    oQueResponde: "Quais assuntos e sistemas os clientes mais abriram no período.",
    custo: { segundos: 2, linhas: 2581 },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "assuntoNome", rotulo: "Assunto" },
        { chave: "sistemaNome", rotulo: "Sistema" },
        qtd(),
      ],
    },
    graficos: [{ rotulo: "sistemaNome", valor: "quantidade" }],
  },
  {
    chave: "acesso-veiculo",
    nome: "Acesso por veículo",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/veiculo",
    filtros: ["periodo", "assunto", "categoriaVeiculo"],
    oQueResponde: "Quais veículos os clientes mais consultaram.",
    custo: { segundos: 2, linhas: 1012 },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "marcaNome", rotulo: "Marca", largura: 160 },
        { chave: "veiculoModelo", rotulo: "Veículo" },
        qtd(),
      ],
    },
    graficos: [{ rotulo: "veiculoModelo", valor: "quantidade" }],
  },
  {
    chave: "acesso-montadora",
    nome: "Montadora e veículo",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/montadora",
    filtros: ["periodo"],
    oQueResponde: "O consumo por veículo, sem separar por assunto.",
    custo: { segundos: 1, linhas: 1030 },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "veiculoModelo", rotulo: "Veículo" },
        qtd(),
      ],
    },
    graficos: [{ rotulo: "veiculoModelo", valor: "quantidade" }],
  },
  {
    chave: "crescimento-assunto",
    nome: "Crescimento de um assunto",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/crescimento-assunto",
    // Nao tem periodo: a serie e historica, do inicio ao fim do que existe.
    filtros: ["categoriaVeiculo", "assunto", "sistema"],
    oQueResponde: "Como o consumo de um assunto e sistema evoluiu, mês a mês.",
    ressalva:
      "Os três campos são obrigatórios — categoria, assunto e sistema. Sem os " +
      "três a API recusa, e é o único relatório que não tem período: a série é " +
      "histórica inteira.",
    custo: { segundos: 2, linhas: 120 },
    saida: {
      forma: "series",
      series: [
        { chave: "crescimento", rotulo: "Mês a mês", colunas: [
          { chave: "ano", rotulo: "Ano", numero: true, ano: true, largura: 80 },
          { chave: "mes", rotulo: "Mês", numero: true, largura: 70 },
          qtd(),
        ] },
        { chave: "assuntoSistema", rotulo: "No assunto e sistema escolhidos", colunas: [
          { chave: "ano", rotulo: "Ano", numero: true, ano: true, largura: 80 },
          { chave: "mes", rotulo: "Mês", numero: true, largura: 70 },
          qtd(),
        ] },
      ],
    },
    // As duas séries do legado, que tem um <canvas> para cada. Aqui a leitura é
    // do tempo, e não do maior: os pontos vão na posição real do calendário --
    // a série do assunto tem 2018-03, 2021-02 e 2021-05, e desenhá-los lado a
    // lado diria que o consumo caiu de um mês para o outro.
    graficos: [
      { tipo: "tempo", de: "crescimento", valor: "quantidade", titulo: "Mês a mês" },
      { tipo: "tempo", de: "assuntoSistema", valor: "quantidade",
        titulo: "No assunto e sistema escolhidos" },
    ],
  },
  {
    chave: "acesso-arquivo",
    nome: "Abertura de arquivo, dia a dia",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/arquivo-diario-mensal",
    filtros: ["periodo"],
    oQueResponde: "Quantos arquivos foram abertos por dia e por mês.",
    custo: { segundos: 1, linhas: 42 },
    saida: {
      forma: "series",
      series: [
        { chave: "mediaDia", rotulo: "Por dia", colunas: [
          { chave: "dia", rotulo: "Dia", numero: true, largura: 70 },
          { chave: "mes", rotulo: "Mês", numero: true, largura: 70 },
          { chave: "ano", rotulo: "Ano", numero: true, ano: true, largura: 80 },
          qtd(),
        ] },
        { chave: "mediaMes", rotulo: "Por mês", colunas: [
          { chave: "mes", rotulo: "Mês", numero: true, largura: 70 },
          { chave: "ano", rotulo: "Ano", numero: true, ano: true, largura: 80 },
          qtd(),
        ] },
      ],
    },
  },
  {
    chave: "acesso-estado",
    nome: "Acesso por estado",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/estado",
    filtros: ["periodo"],
    oQueResponde: "De onde vêm os acessos, pelo DDD do telefone do cliente.",
    custo: { segundos: 2, linhas: 2098 },
    saida: {
      forma: "series",
      series: [
        { chave: "mediaMes", rotulo: "Por mês", colunas: [
          { chave: "ddd", rotulo: "DDD", largura: 80 },
          { chave: "mes", rotulo: "Mês", numero: true, largura: 70 },
          { chave: "ano", rotulo: "Ano", numero: true, ano: true, largura: 80 },
          qtd(),
        ] },
        { chave: "mediaDia", rotulo: "Por dia", colunas: [
          { chave: "ddd", rotulo: "DDD", largura: 80 },
          { chave: "dia", rotulo: "Dia", numero: true, largura: 70 },
          { chave: "mes", rotulo: "Mês", numero: true, largura: 70 },
          qtd(),
        ] },
      ],
    },
  },
  {
    chave: "acesso-cliente",
    nome: "Cliente por dia e mês",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/cliente-dia-mes",
    filtros: ["periodo"],
    oQueResponde: "Quanto cada cliente consumiu, dia a dia.",
    custo: { segundos: 1, linhas: 15638 },
    ressalva: "É o maior desta família: 15 mil linhas num mês. Vale filtrar por período curto.",
    saida: {
      forma: "series",
      series: [
        { chave: "mediaMes", rotulo: "Por mês", colunas: [
          { chave: "nome", rotulo: "Cliente" },
          { chave: "mes", rotulo: "Mês", numero: true, largura: 70 },
          qtd(),
        ] },
        { chave: "mediaDia", rotulo: "Por dia", colunas: [
          { chave: "nome", rotulo: "Cliente" },
          { chave: "dia", rotulo: "Dia", numero: true, largura: 70 },
          qtd(),
        ] },
      ],
    },
  },
  {
    chave: "quantidade-acesso",
    nome: "Acessos por cliente",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/quantidade-acesso",
    filtros: ["periodo"],
    oQueResponde: "Quantas vezes cada cliente achou e quantas não achou o que procurava.",
    custo: { segundos: 1, linhas: 3471 },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "nome", rotulo: "Cliente" },
        { chave: "qtdAcesso", rotulo: "Achou", numero: true, largura: 110 },
        { chave: "qtdNaoEncontrada", rotulo: "Não achou", numero: true, largura: 110 },
      ],
    },
  },
  {
    chave: "sem-consulta",
    nome: "Acesso sem consulta",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/sem-consulta",
    filtros: ["periodo", "cliente"],
    oQueResponde: "O que os clientes procuraram e não encontraram.",
    custo: { segundos: 8, linhas: 32254 },
    ressalva:
      "O mais pesado de todos: 8 segundos e 7 MB num mês, com 32 mil buscas. " +
      "Escolha um período curto ou um cliente antes de rodar.",
    detalhe: {
      rota: "api/relatorios/acesso/sem-consulta/{id}/detalhe",
      campoId: "buscaId",
      titulo: "Detalhe da busca",
      nota:
        "A linha já traz quase tudo. O detalhe acrescenta os ids de marca, " +
        "assunto e sistema que a busca carregava — úteis para ir olhar o " +
        "cadastro, mas sem nome nenhum junto.",
    },
    saida: {
      forma: "composta",
      blocos: [
        { rotulo: "Buscas", de: "itens", colunas: [
          { chave: "clienteNome", rotulo: "Cliente" },
          // A API chama o campo de `termo`. Estava escrito `textoBusca` aqui, e
          // a coluna inteira saia "--" -- o unico dado que a tela existe para
          // mostrar, vazio, sem erro nenhum para denunciar.
          { chave: "termo", rotulo: "Procurou" },
          { chave: "veiculoModelo", rotulo: "Veiculo" },
          { chave: "motorNome", rotulo: "Motor" },
          { chave: "data", rotulo: "Quando", largura: 150 },
        ] },
      ],
    },
  },
  {
    chave: "nao-encontrado",
    nome: "Conteúdo não encontrado",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/acesso/conteudo-nao-encontrado",
    filtros: ["periodo"],
    oQueResponde: "Que combinação de veículo, assunto e sistema falta no acervo.",
    custo: { segundos: 1, linhas: 2 },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "veiculoModelo", rotulo: "Veículo" },
        { chave: "motorNome", rotulo: "Motor" },
        { chave: "ano", rotulo: "Ano", numero: true, ano: true, largura: 80 },
        { chave: "assuntoNome", rotulo: "Assunto" },
        { chave: "sistemaNome", rotulo: "Sistema" },
        qtd("Pedidos"),
      ],
    },
  },

  // ---------------------------------------------------------- arquivos
  {
    chave: "arquivos-acessados",
    nome: "Arquivos mais acessados",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/arquivos-mais-acessados",
    filtros: ["periodo"],
    oQueResponde: "Que material foi mais aberto.",
    custo: { segundos: 1, linhas: 20 },
    saida: {
      forma: "composta",
      blocos: [
        { rotulo: "Arquivos", de: "itens", colunas: [
          { chave: "dadoDesc", rotulo: "Conteúdo" },
          { chave: "produtoNome", rotulo: "Produto", largura: 180 },
          { chave: "quantidade", rotulo: "Aberturas", numero: true, largura: 110 },
          { chave: "pontos", rotulo: "Pontos", numero: true, largura: 90 },
        ] },
      ],
    },
  },
  {
    chave: "usuarios-acessaram",
    nome: "Quem mais acessou",
    grupo: "Acesso e consumo",
    rota: "api/relatorios/usuarios-mais-acessaram",
    filtros: ["periodo"],
    oQueResponde: "Os clientes que mais abriram material no período.",
    custo: { segundos: 1, linhas: 20 },
    saida: {
      forma: "composta",
      blocos: [
        { rotulo: "Clientes", de: "itens", colunas: [
          { chave: "nome", rotulo: "Cliente" },
          { chave: "quantidade", rotulo: "Aberturas", numero: true, largura: 110 },
        ] },
      ],
    },
  },

  // ---------------------------------------------------------- cobertura
  {
    chave: "veiculo-assunto-sistema",
    nome: "Veículo × assunto × sistema",
    grupo: "Cobertura do acervo",
    rota: "api/relatorios/veiculo-assunto-sistema",
    filtros: ["marca", "assunto", "sistema"],
    oQueResponde: "Para uma marca, onde existe e onde falta conteúdo.",
    custo: { segundos: 1, linhas: 46 },
    saida: {
      forma: "composta",
      blocos: [
        { rotulo: "Cobertura", de: "itens", colunas: [
          { chave: "veiculoModelo", rotulo: "Veículo" },
          { chave: "motorNome", rotulo: "Motor" },
          { chave: "anoIni", rotulo: "De", numero: true, ano: true, largura: 70 },
          { chave: "anoFim", rotulo: "Até", numero: true, ano: true, largura: 70 },
          { chave: "existe", rotulo: "Tem conteúdo", largura: 120 },
        ] },
      ],
    },
  },
  {
    chave: "categoria-veiculo-assunto-sistema",
    nome: "Categoria × assunto × sistema",
    grupo: "Cobertura do acervo",
    rota: "api/relatorios/categoria-veiculo-assunto-sistema",
    filtros: ["categoriaVeiculo", "assunto", "sistema"],
    oQueResponde: "O mesmo cruzamento, para uma categoria inteira de veículos.",
    custo: { segundos: 45, linhas: 13396 },
    ressalva:
      "Levou 45 segundos e trouxe 13 mil linhas na medição. É a consulta mais cara " +
      "do módulo — rode com o filtro mais estreito que der.",
    saida: {
      forma: "composta",
      blocos: [
        { rotulo: "Cobertura", de: "itens", colunas: [
          { chave: "veiculoModelo", rotulo: "Veículo" },
          { chave: "motorNome", rotulo: "Motor" },
          { chave: "anoIni", rotulo: "De", numero: true, ano: true, largura: 70 },
          { chave: "anoFim", rotulo: "Até", numero: true, ano: true, largura: 70 },
          { chave: "existe", rotulo: "Tem conteúdo", largura: 120 },
        ] },
      ],
    },
  },

  // ---------------------------------------------------------- DiagIA
  {
    chave: "ia-consumo",
    nome: "Consumo da DiagIA em dinheiro",
    grupo: "DiagIA",
    rota: "api/relatorios/ia/consumo",
    filtros: ["periodo", "cliente"],
    oQueResponde:
      "Quanto cada pessoa gastou de IA no período — por modelo, por sessão, em dólar e em real.",
    ressalva:
      "O preço é o vigente na data de cada consumo, não o de hoje. A taxa do dólar vem da " +
      "IACambio, que hoje tem uma linha só aplicada a todo o histórico — o valor em real é " +
      "aproximado, e a taxa usada aparece no cabeçalho.",
    custo: { segundos: 3, linhas: 200 },
    painel: {
      campos: [
        { chave: "cobertura.perguntas", rotulo: "Perguntas no período" },
        { chave: "cobertura.medidas", rotulo: "Com token gravado" },
        { chave: "cobertura.percentual", rotulo: "Cobertura", sufixo: "%" },
        { chave: "taxaDoDolar", rotulo: "Dólar usado" },
      ],
      alerta: {
        chave: "cobertura.confiavel",
        texto:
          "Boa parte das perguntas do período não tem token gravado — o custo abaixo é " +
          "só do pedaço medido, e não serve para cobrar. A gravação chegou a 92% em maio " +
          "e junho de 2026 e caiu depois; o conserto é na ApiDiagweb, não aqui.",
      },
    },
    saida: {
      forma: "composta",
      blocos: [
        { rotulo: "Por modelo", de: "porModelo", colunas: [
          { chave: "modelo", rotulo: "Modelo" },
          { chave: "fornecedor", rotulo: "Fornecedor", largura: 110 },
          { chave: "chamadas", rotulo: "Chamadas", numero: true, largura: 100 },
          { chave: "entrada", rotulo: "Tokens de entrada", numero: true, largura: 140 },
          { chave: "saida", rotulo: "Tokens de saída", numero: true, largura: 130 },
          { chave: "custoUsd", rotulo: "US$", numero: true, largura: 100 },
          { chave: "custoBrl", rotulo: "R$", numero: true, largura: 100 },
        ] },
        { rotulo: "Por pessoa", de: "porCliente", colunas: [
          { chave: "clienteNome", rotulo: "Pessoa" },
          { chave: "clienteId", rotulo: "Cliente", numero: true, ano: true, largura: 90 },
          { chave: "sessoes", rotulo: "Sessões", numero: true, largura: 90 },
          { chave: "chamadas", rotulo: "Chamadas", numero: true, largura: 100 },
          { chave: "entrada", rotulo: "Entrada", numero: true, largura: 120 },
          { chave: "saida", rotulo: "Saída", numero: true, largura: 110 },
          { chave: "custoBrl", rotulo: "R$", numero: true, largura: 100 },
        ] },
        { rotulo: "Por sessão", de: "porSessao", colunas: [
          { chave: "conversaId", rotulo: "Sessão", numero: true, ano: true, largura: 90 },
          { chave: "data", rotulo: "Quando", largura: 150 },
          { chave: "clienteNome", rotulo: "Pessoa" },
          { chave: "veiculo", rotulo: "Veículo" },
          { chave: "modelos", rotulo: "Modelos" },
          { chave: "chamadas", rotulo: "Chamadas", numero: true, largura: 100 },
          { chave: "custoBrl", rotulo: "R$", numero: true, largura: 100 },
        ] },
      ],
    },
  },
  {
    chave: "ia-token",
    nome: "DiagIA · consumo de token",
    grupo: "DiagIA",
    rota: "api/relatorios/ia/token",
    filtros: ["periodo"],
    oQueResponde: "Quanto cada modelo e cada cliente consumiu de token.",
    custo: { segundos: 1, linhas: 334 },
    saida: {
      forma: "series",
      series: [
        { chave: "porModelo", rotulo: "Por modelo", colunas: [
          { chave: "modelo", rotulo: "Modelo" },
          { chave: "qtdConsultas", rotulo: "Consultas", numero: true, largura: 100 },
          { chave: "totalInput", rotulo: "Entrada", numero: true, largura: 120 },
          { chave: "totalOutput", rotulo: "Saída", numero: true, largura: 120 },
          { chave: "totalTokens", rotulo: "Total", numero: true, largura: 120 },
        ] },
        { chave: "porCliente", rotulo: "Por cliente", colunas: [
          { chave: "clienteNome", rotulo: "Cliente" },
          { chave: "modelo", rotulo: "Modelo", largura: 200 },
          { chave: "totalTokens", rotulo: "Total", numero: true, largura: 120 },
        ] },
      ],
    },
  },
  {
    chave: "ia-log",
    nome: "DiagIA · log de consultas",
    grupo: "DiagIA",
    rota: "api/relatorios/ia/log",
    filtros: ["periodo", "somenteErros"],
    oQueResponde: "Cada consulta à IA, com duração e erro.",
    custo: { segundos: 1, linhas: 579 },
    ressalva:
      "Isto é operação, não auditoria: mede duração e erro, e vê só uma fração " +
      "do movimento — 114 conversas num mês, contra 39.389 na tabela de " +
      "conversas. Para ler o que foi perguntado e respondido, use " +
      "Mais › Atendimento › Conversas da DiagIA.",
    detalhe: {
      rota: "api/relatorios/ia/log/{id}/detalhe",
      campoId: "conversaIAId",
      titulo: "A conversa",
      nota:
        "A rota quer o id da CONVERSA, não o do log — são campos diferentes na " +
        "mesma linha, e trocá-los traz a conversa errada sem dar erro.",
    },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "clienteNome", rotulo: "Cliente" },
        { chave: "inicio", rotulo: "Início", largura: 160 },
        { chave: "duracaoSegundos", rotulo: "Duração (s)", numero: true, largura: 110 },
        { chave: "erro", rotulo: "Erro" },
      ],
    },
  },

  // ---------------------------------------------------------- feedback
  {
    chave: "feedback",
    nome: "Feedback do novo Diagweb",
    grupo: "Feedback",
    rota: "api/relatorios/feedback",
    filtros: ["periodo"],
    oQueResponde: "O que os clientes disseram pelo botão de feedback do site novo.",
    custo: { segundos: 1, linhas: 12 },
    ressalva: "Módulo recente: há 66 registros no total, todos a partir de julho de 2026.",
    maximoDias: 366,
    saida: {
      forma: "composta",
      blocos: [
        { rotulo: "Distribuição", de: "distribuicao", colunas: [
          { chave: "nome", rotulo: "Tipo" },
          { chave: "quantidade", rotulo: "Quantidade", numero: true, largura: 120 },
        ] },
        { rotulo: "Mensagens", de: "feedbacks", colunas: [
          { chave: "tipoNome", rotulo: "Tipo", largura: 120 },
          { chave: "mensagem", rotulo: "Mensagem" },
          { chave: "criadoEm", rotulo: "Quando", largura: 150 },
        ] },
      ],
    },
  },

  // ---------------------------------------------------------- comercial
  {
    chave: "comercial-quadro",
    nome: "Quadro do período",
    grupo: "Comercial",
    rota: "api/relatorios/comercial/quadro",
    filtros: ["periodo"],
    oQueResponde: "Os números do período num painel só.",
    custo: { segundos: 1, linhas: 7 },
    maximoDias: 366,
    saida: {
      forma: "tiles",
      campos: [
        { chave: "qtdArquivos", rotulo: "Arquivos abertos" },
        { chave: "qtdArquivosLicenca", rotulo: "Por licença" },
        { chave: "qtdArquivosImpressos", rotulo: "Impressos" },
        { chave: "qtdAcessos", rotulo: "Acessos" },
        { chave: "qtdCadastros", rotulo: "Cadastros" },
        { chave: "qtdSuporteTecnico", rotulo: "Suporte técnico" },
        { chave: "qtdSuporteFaleConosco", rotulo: "Fale conosco" },
      ],
    },
  },
  {
    chave: "especialidade-licenca",
    nome: "Licenças por especialidade",
    grupo: "Comercial",
    rota: "api/relatorios/comercial/especialidade-licenca",
    filtros: [],
    oQueResponde: "Quantos clientes e licenças há em cada especialidade de oficina.",
    custo: { segundos: 2, linhas: 21 },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "especialidade", rotulo: "Especialidade" },
        { chave: "qtd", rotulo: "Clientes", numero: true, largura: 100 },
        { chave: "qtdLicencas", rotulo: "Licenças", numero: true, largura: 100 },
        { chave: "qtdLicencasAdimplente", rotulo: "Adimplentes", numero: true, largura: 110 },
        { chave: "qtdLicencasInadimplente", rotulo: "Inadimplentes", numero: true, largura: 120 },
        { chave: "qtdLicencasRenovaram", rotulo: "Renovaram", numero: true, largura: 110 },
      ],
    },
    graficos: [{ rotulo: "especialidade", valor: "qtdLicencas" }],
  },
  {
    chave: "especialidade-licenca-estado",
    nome: "Licenças por especialidade e estado",
    grupo: "Comercial",
    rota: "api/relatorios/comercial/especialidade-licenca/por-estado",
    // POST porque leva duas LISTAS no corpo -- nao porque grava. A trava do
    // canario sabe disso; ver ESCRITAS_QUE_NAO_GRAVAM em api/trava.ts.
    metodo: "post",
    filtros: ["especialidades", "estados"],
    oQueResponde: "A mesma quebra de licenças, recortada por estado.",
    ressalva:
      "Escolha ao menos uma especialidade e um estado — a API recusa vazio. As " +
      "especialidades são o texto que o cliente escolheu no cadastro ao longo " +
      "dos anos, então a lista tem duplicata de grafia (\"Injeção Eletrônica\" e " +
      "\"Injeção eletrônica\"), uma vazia e uma literalmente \"undefined\". Não é " +
      "erro da tela: é o que está gravado.",
    custo: { segundos: 3, linhas: 60 },
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "estado", rotulo: "UF", largura: 60 },
        { chave: "especialidade", rotulo: "Especialidade" },
        { chave: "qtd", rotulo: "Clientes", numero: true, largura: 100 },
        { chave: "qtdLicencas", rotulo: "Licenças", numero: true, largura: 100 },
        { chave: "qtdLicencasAdimplente", rotulo: "Adimplentes", numero: true, largura: 110 },
        { chave: "qtdLicencasInadimplente", rotulo: "Inadimplentes", numero: true, largura: 120 },
        { chave: "qtdLicencasRenovaram", rotulo: "Renovaram", numero: true, largura: 110 },
      ],
    },
  },
  {
    chave: "especialidade-acesso",
    nome: "Acesso por especialidade",
    grupo: "Comercial",
    rota: "api/relatorios/comercial/especialidade-acesso",
    filtros: ["periodo"],
    oQueResponde: "Que especialidade de oficina mais consumiu no período.",
    custo: { segundos: 2, linhas: 22 },
    maximoDias: 366,
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "especialidade", rotulo: "Especialidade" },
        { chave: "qtd", rotulo: "Clientes", numero: true, largura: 100 },
        { chave: "qtdLicenca", rotulo: "Com licença", numero: true, largura: 110 },
        { chave: "qtdProdutos", rotulo: "Com produto", numero: true, largura: 110 },
        { chave: "qtdGratuito", rotulo: "Gratuito", numero: true, largura: 100 },
      ],
    },
    graficos: [{ rotulo: "especialidade", valor: "qtd" }],
  },
  {
    chave: "especialidade-acesso-estado",
    nome: "Acesso por especialidade e estado",
    grupo: "Comercial",
    rota: "api/relatorios/comercial/especialidade-acesso/por-estado",
    filtros: ["periodo"],
    oQueResponde: "O mesmo, aberto por estado.",
    custo: { segundos: 1, linhas: 74 },
    maximoDias: 366,
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "estado", rotulo: "Estado", largura: 90 },
        { chave: "especialidade", rotulo: "Especialidade" },
        { chave: "qtd", rotulo: "Clientes", numero: true, largura: 100 },
        { chave: "qtdLicenca", rotulo: "Com licença", numero: true, largura: 110 },
      ],
    },
  },
  {
    chave: "renovacao",
    nome: "Renovações",
    grupo: "Comercial",
    rota: "api/relatorios/comercial/renovacao",
    filtros: ["periodo"],
    oQueResponde: "Quem renovou e quem não renovou no período.",
    custo: { segundos: 1, linhas: 2 },
    maximoDias: 366,
    ressalva:
      "Depende de contrato com valor, e no banco isso só existe até 2020 — " +
      "de 2021 em diante não há nenhum. Períodos recentes vêm quase vazios, e isso é o dado.",
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "tipoCliente", rotulo: "Tipo de cliente" },
        { chave: "qtd", rotulo: "Total", numero: true, largura: 90 },
        { chave: "renovaram", rotulo: "Renovaram", numero: true, largura: 110 },
        { chave: "naoRenovaram", rotulo: "Não renovaram", numero: true, largura: 130 },
      ],
    },
  },
  {
    chave: "licencas",
    nome: "Licenças vendidas",
    grupo: "Comercial",
    rota: "api/relatorios/comercial/licencas",
    filtros: ["periodo"],
    oQueResponde: "Licenças novas e renovadas no período, por tipo de cliente.",
    custo: { segundos: 1, linhas: 0 },
    maximoDias: 366,
    ressalva:
      "Conta contratos pagos (licença 2, valor acima de zero). No banco esses contratos " +
      "param em 2020: 2.424 no total, nenhum depois disso. Em qualquer período recente " +
      "este relatório vem vazio — não é defeito da tela, é o que há.",
    saida: {
      forma: "tabela",
      colunas: [
        { chave: "tipoCliente", rotulo: "Tipo de cliente" },
        { chave: "qtd", rotulo: "Total", numero: true, largura: 90 },
        { chave: "novo", rotulo: "Novos", numero: true, largura: 90 },
        { chave: "renovacao", rotulo: "Renovações", numero: true, largura: 110 },
        { chave: "adimplente", rotulo: "Adimplentes", numero: true, largura: 110 },
        { chave: "inadimplente", rotulo: "Inadimplentes", numero: true, largura: 120 },
      ],
    },
  },
]

export const GRUPOS = [...new Set(RELATORIOS.map((r) => r.grupo))]

/**
 * A permissão reproduz o acesso de hoje: no sistema antigo, os relatórios de
 * conteúdo ficam sob a chave `conteudo` e os comerciais sob `relatorio`. Trocar
 * isso por uma chave só tiraria o acesso de quem tem um e não o outro.
 */
export function chaveDeAcesso(grupo: string): string {
  return grupo === "Comercial" ? "relatorio" : "conteudo"
}
